import React from 'react';
import {
    Show,
    SimpleShowLayout,
    DateField,
    ReferenceField,
    FunctionField,
    useTranslate,
    useRecordContext,
    useNotify,
    useRefresh,
    useDataProvider,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { Cancel, CheckCircle, Pending, Info, Assignment, ArrowBack, DeleteOutline } from '@mui/icons-material';

const tableCellWrapSx = {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
    verticalAlign: 'top',
};

const detailsTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    '& .MuiTableCell-root': {
        ...tableCellWrapSx,
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.25 },
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
    },
};

const wrappedChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const MobileCancelSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [open, setOpen] = React.useState(false);
    const [cancelling, setCancelling] = React.useState(false);

    if (!record || record.status !== 'pending') return null;

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await dataProvider.update('fee-template-changes', {
                id: record.id,
                data: { status: 'cancelled' },
                previousData: record,
            });
            notify('resources.fee-template-changes.notifications.cancelled', { type: 'success' });
            refresh();
            setOpen(false);
        } catch (error: any) {
            notify('resources.fee-template-changes.notifications.cancelFailed', { type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    return (
        <>
            <Card sx={{ mt: 2, mb: 10, overflow: 'hidden', border: '1px solid', borderColor: 'error.light' }}>
                <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <DeleteOutline sx={{ color: 'error.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                            {translate('resources.fee-template-changes.actions.cancelTitle', { _: 'Danger Zone' })}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {translate('resources.fee-template-changes.actions.cancelConfirm', { _: 'This action cannot be undone.' })}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => setOpen(true)}
                        sx={{
                            width: '100%',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            py: 1.5,
                            borderColor: '#D32F2F',
                            color: '#D32F2F',
                            '&:hover': { borderColor: '#B71C1C', backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                        }}
                    >
                        {translate('resources.fee-template-changes.actions.cancel')}
                    </Button>
                </CardContent>
            </Card>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>{translate('resources.fee-template-changes.actions.cancelTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{translate('resources.fee-template-changes.actions.cancelConfirm')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={cancelling}>{translate('ra.action.cancel')}</Button>
                    <Button onClick={handleCancel} color="error" variant="contained" disabled={cancelling} autoFocus>
                        {translate('resources.fee-template-changes.actions.confirmCancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const PageHeader = ({ isMobile }: { isMobile: boolean }) => {
    const translate = useTranslate();
    const redirect = useRedirect();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 2, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/fee-template-changes')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('ra.action.back')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1.25rem', sm: '2.125rem' },
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        }}>
                            {translate('resources.fee-template-changes.show.title')}
                        </Typography>
                        <StatusDisplay />
                    </Box>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                        {translate('resources.fee-template-changes.show.subtitle')}
                    </Typography>
                </Box>
            </Box>

            {!isMobile && (
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <CancelButton />
                </Box>
            )}
        </Box>
    );
};

const StatusDisplay = () => {
    const translate = useTranslate();
    const record = useRecordContext();

    if (!record) return null;

    const now = new Date();
    const effectiveDate = new Date(record.effectiveDate);

    let status = record.status || 'pending';
    let icon = <Pending />;
    let color: 'warning' | 'success' | 'error' | 'default' = 'warning';

    switch (status) {
        case 'cancelled':
            icon = <Cancel />;
            color = 'error';
            break;
        case 'applied':
            icon = <CheckCircle />;
            color = 'success';
            break;
        case 'pending':
            if (effectiveDate < now) {
                status = 'overdue';
                color = 'error';
            }
            break;
    }

    return (
        <Chip
            label={translate(`resources.fee-template-changes.status.${status}`)}
            color={color}
            icon={icon}
            size="medium"
            component="div"
            clickable={false}
            sx={{
                cursor: 'default',
                fontWeight: 600,
                px: 1,
                ...wrappedChipSx,
            }}
        />
    );
};

const CancelButton = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [open, setOpen] = React.useState(false);
    const [cancelling, setCancelling] = React.useState(false);

    if (!record || record.status !== 'pending') return null;

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            console.log('[FeeTemplateChangesShow] Attempting to cancel change:', record.id);

            await dataProvider.update('fee-template-changes', {
                id: record.id,
                data: { status: 'cancelled' },
                previousData: record,
            });

            notify('resources.fee-template-changes.notifications.cancelled', { type: 'success' });
            refresh();
            handleClose();
        } catch (error: any) {
            console.error('[FeeTemplateChangesShow] Cancel error:', error);
            console.error('[FeeTemplateChangesShow] Error details:', {
                message: error.message,
                status: error.status,
                name: error.name,
                stack: error.stack
            });
            notify('resources.fee-template-changes.notifications.cancelFailed', { type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={handleClickOpen}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    borderColor: '#F44336',
                    color: '#F44336',
                    '&:hover': {
                        borderColor: '#D32F2F',
                        backgroundColor: 'rgba(244, 67, 54, 0.04)',
                    },
                    width: { xs: '100%', sm: 'auto' },
                }}
            >
                {translate('resources.fee-template-changes.actions.cancel')}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="cancel-dialog-title"
                aria-describedby="cancel-dialog-description"
            >
                <DialogTitle id="cancel-dialog-title">
                    {translate('resources.fee-template-changes.actions.cancelTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="cancel-dialog-description">
                        {translate('resources.fee-template-changes.actions.cancelConfirm')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={cancelling}>
                        {translate('ra.action.cancel')}
                    </Button>
                    <Button
                        onClick={handleCancel}
                        color="error"
                        variant="contained"
                        disabled={cancelling}
                        autoFocus
                    >
                        {translate('resources.fee-template-changes.actions.confirmCancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const FeeStructureComparison = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!record || !record.newStructure) return null;

    let newStructure: any;
    let currentStructure: any;
    try {
        newStructure = typeof record.newStructure === 'string'
            ? JSON.parse(record.newStructure)
            : record.newStructure;

        if (record.template?.currentStructure) {
            currentStructure = typeof record.template.currentStructure === 'string'
                ? JSON.parse(record.template.currentStructure)
                : record.template.currentStructure;
        }
    } catch (error) {
        console.error('Failed to parse structure:', error);
        return null;
    }

    const renderComponentStatus = (component: any) => {
        if (!component) return '-';
        return component.enabled ? (
            <Chip label={translate('common.enabled')} color="success" size="small" sx={wrappedChipSx} />
        ) : (
            <Chip label={translate('common.disabled')} color="default" size="small" sx={wrappedChipSx} />
        );
    };

    const components = newStructure.components || {};

    const structuresAreIdentical = currentStructure &&
        JSON.stringify(currentStructure) === JSON.stringify(newStructure);

    const componentRows = [
        {
            key: 'sessionFee',
            label: translate('resources.fee-templates.components.sessionFee'),
            config: components.sessionFee?.enabled && components.sessionFee.amount
                ? `${newStructure.currency} ${components.sessionFee.amount}`
                : '-',
        },
        {
            key: 'energyFee',
            label: translate('resources.fee-templates.components.energyFee'),
            config: components.energyFee?.enabled && components.energyFee.rates?.length > 0
                ? components.energyFee.rates
                    .map((rate: any) => `${rate.name}: ${newStructure.currency} ${rate.rate}/${rate.unit || 'kWh'}`)
                    .join('; ')
                : '-',
        },
        {
            key: 'timeFee',
            label: translate('resources.fee-templates.components.timeFee'),
            config: components.timeFee?.enabled && components.timeFee.rates?.length > 0
                ? components.timeFee.rates
                    .map((rate: any) => `${rate.name ? rate.name + ': ' : ''}${newStructure.currency} ${rate.rate}/${components.timeFee.unit || 'minute'}`)
                    .join('; ')
                : '-',
        },
        {
            key: 'idleFee',
            label: translate('resources.fee-templates.components.idleFee'),
            config: components.idleFee?.enabled && components.idleFee.rate
                ? `${newStructure.currency} ${components.idleFee.rate}/min (${components.idleFee.graceMinutes}min grace)`
                : '-',
        },
        {
            key: 'reservationFee',
            label: translate('resources.fee-templates.components.reservationFee'),
            config: components.reservationFee?.enabled && components.reservationFee.amount
                ? `${newStructure.currency} ${components.reservationFee.amount}`
                : '-',
        },
    ];

    return (
        <>
            {structuresAreIdentical && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('resources.fee-template-changes.show.noChangesDetected')}
                </Alert>
            )}
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {componentRows.map((row) => (
                        <Paper key={row.key} variant="outlined" sx={{ p: 2, minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, mb: 1, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                                {row.label}
                            </Typography>
                            <Box sx={{ mb: 0.5 }}>
                                {renderComponentStatus(components[row.key])}
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                            >
                                {row.config}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    }}
                >
                    <Table size="small" sx={detailsTableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('resources.fee-template-changes.fields.component')}</TableCell>
                                <TableCell>{translate('resources.fee-template-changes.fields.status')}</TableCell>
                                <TableCell>{translate('resources.fee-template-changes.fields.configuration')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {componentRows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell>{renderComponentStatus(components[row.key])}</TableCell>
                                    <TableCell>{row.config}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
};

export const FeeTemplateChangesShow = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                maxWidth: '100%',
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    maxWidth: '100%',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    maxWidth: '100%',
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', minWidth: 0 }}>
                    <PageHeader isMobile={isMobile} />

                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.fee-template-changes.show.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {translate('resources.fee-template-changes.fields.template')}
                                    </Typography>
                                    <ReferenceField source="templateId" reference="fee-templates">
                                        <FunctionField
                                            render={(template: any) => (
                                                <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    {template?.name || '-'}
                                                </Typography>
                                            )}
                                        />
                                    </ReferenceField>
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {translate('resources.fee-template-changes.fields.effectiveDate')}
                                    </Typography>
                                    <DateField source="effectiveDate" showTime />
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {translate('resources.fee-template-changes.fields.createdBy')}
                                    </Typography>
                                    <ReferenceField source="createdBy" reference="backend-users">
                                        <FunctionField
                                            render={(user: any) => (
                                                <Typography sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : '-'}
                                                </Typography>
                                            )}
                                        />
                                    </ReferenceField>
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        {translate('resources.fee-template-changes.fields.createdAt')}
                                    </Typography>
                                    <DateField source="createdAt" showTime />
                                </Box>
                            </Box>

                            <FunctionField
                                render={(record: any) => {
                                    if (!record?.reason) return null;
                                    return (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                {translate('resources.fee-template-changes.fields.reason')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                {record.reason}
                                            </Typography>
                                        </Box>
                                    );
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Assignment sx={{ mr: 2, color: '#00C853' }} />
                                <FunctionField
                                    render={(record: any) => {
                                        const titleKey = record.status === 'applied'
                                            ? 'resources.fee-template-changes.show.changeDetailsApplied'
                                            : 'resources.fee-template-changes.show.changeDetails';
                                        return (
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {translate(titleKey)}
                                            </Typography>
                                        );
                                    }}
                                />
                            </Box>

                            <FeeStructureComparison />

                            <FunctionField
                                render={(record: any) => {
                                    if (record.status === 'applied' && record.appliedAt) {
                                        return (
                                            <Alert severity="success" sx={{ mt: 3 }}>
                                                {translate('resources.fee-template-changes.show.appliedAt', {
                                                    date: new Date(record.appliedAt).toLocaleString()
                                                })}
                                            </Alert>
                                        );
                                    }
                                    if (record.status === 'cancelled') {
                                        return (
                                            <Alert severity="error" sx={{ mt: 3 }}>
                                                {translate('resources.fee-template-changes.show.cancelled')}
                                            </Alert>
                                        );
                                    }
                                    return null;
                                }}
                            />
                        </CardContent>
                    </Card>

                    {isMobile && <MobileCancelSection />}
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};
